/**
 * useApiQuery — wrapper resilient pour fetch GET
 * ---------------------------------------------------------------------------
 * Avantages vs fetch+try/catch nu :
 *  • Auto-retry silencieux (par defaut 1x apres 1500ms) sur erreurs reseau
 *    et codes 502/503/504 (gateway transient).
 *  • Parse JSON safe : un body non-JSON (HTML 502 du proxy) ne fait pas crasher
 *    le composant.
 *  • Distingue clairement les natures d'erreur via { type, status, detail } :
 *      - 'network'   : pas de reponse (offline, DNS, CORS)
 *      - 'auth'      : 401
 *      - 'gateway'   : 502/503/504 epuise
 *      - 'http'      : autre erreur HTTP, message lit le `detail` FastAPI
 *  • Bearer token recupere automatiquement de tokenService.getToken().
 *  • API minimale : `const { data, error, loading, refetch } = useApiQuery('/api/...')`
 *  • Le composant peut decider d'afficher un message friendly via `error.type`.
 *
 * Volontairement minimaliste : pas de cache, pas de dedup, pas de focus refetch.
 * Si vous avez besoin de plus, switchez vers @tanstack/react-query.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { getToken } from '../services/tokenService';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const _DEFAULT = {
  enabled: true,
  retries: 1,
  retryDelayMs: 1500,
  // Si false, ne renvoie aucun header Authorization (utile pour endpoints publics)
  authenticated: true,
};

export function useApiQuery(path, options = {}) {
  const opts = { ..._DEFAULT, ...options };
  const [state, setState] = useState({ data: null, error: null, loading: opts.enabled });
  const abortRef = useRef(null);

  const run = useCallback(async (attempt = 0) => {
    if (!path) return;
    if (abortRef.current) { try { abortRef.current.abort(); } catch {} }
    const controller = new AbortController();
    abortRef.current = controller;

    setState((s) => ({ ...s, loading: true, error: attempt === 0 ? null : s.error }));

    try {
      const headers = { Accept: 'application/json' };
      if (opts.authenticated) {
        const token = getToken();
        if (!token) {
          setState({ data: null, loading: false, error: { type: 'auth', status: 401, detail: 'Session expiree' } });
          return;
        }
        headers.Authorization = `Bearer ${token}`;
      }

      const url = path.startsWith('http') ? path : `${API_URL}${path}`;
      const res = await fetch(url, { headers, signal: controller.signal });

      // V36.44 — Lire le body en TEXTE UNE SEULE FOIS (pattern robuste).
      // L'ancien `res.clone().json()` echoue ("Response body is already used")
      // dans certains cas (React StrictMode re-render, fetch retry interne).
      let bodyText = '';
      try { bodyText = await res.text(); } catch { /* network mid-fetch */ }
      let data = null;
      if (bodyText) {
        try { data = JSON.parse(bodyText); } catch { /* not JSON */ }
      }

      if (res.ok && data !== null) {
        setState({ data, loading: false, error: null });
        return;
      }
      // Si la reponse est OK mais le body n'est PAS du JSON (texte brut, fichier),
      // on ne traite pas comme une erreur : on retourne le data tel quel (peut etre null).
      if (res.ok) {
        setState({ data, loading: false, error: null });
        return;
      }
      if (res.status === 401) {
        setState({ data: null, loading: false, error: { type: 'auth', status: 401, detail: 'Session expiree, veuillez vous reconnecter' } });
        return;
      }
      if ([502, 503, 504].includes(res.status) && attempt < opts.retries) {
        setTimeout(() => run(attempt + 1), opts.retryDelayMs);
        return;
      }
      const isGateway = [502, 503, 504].includes(res.status);
      setState({
        data: null,
        loading: false,
        error: {
          type: isGateway ? 'gateway' : 'http',
          status: res.status,
          detail: data?.detail || `HTTP ${res.status}`,
        },
      });
    } catch (err) {
      if (err?.name === 'AbortError') return;  // pas un vrai echec — silencieux
      if (attempt < opts.retries) {
        setTimeout(() => run(attempt + 1), opts.retryDelayMs);
        return;
      }
      setState({
        data: null,
        loading: false,
        error: { type: 'network', status: 0, detail: 'Erreur de connexion au serveur. Verifiez votre connexion internet.' },
      });
    }
  }, [path, opts.authenticated, opts.retries, opts.retryDelayMs]);

  useEffect(() => {
    if (opts.enabled) run(0);
    return () => { if (abortRef.current) { try { abortRef.current.abort(); } catch {} } };
  }, [path, opts.enabled]);

  const refetch = useCallback(() => run(0), [run]);

  return { ...state, refetch };
}

export default useApiQuery;
